"use client";

import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface CountryCodeSelectorProps {
  value: string;
  onChange: (countryCode: string) => void;
  className?: string;
  suppressLabel?: boolean;
}

// Country name formatter
const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

export function CountryCodeSelector({
  value,
  onChange,
  className,
  suppressLabel = false,
}: CountryCodeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>("IN");

  // Initialize selected country from value prop
  useEffect(() => {
    if (value) {
      // Find country by calling code
      const countries = getCountries();
      const country = countries.find((c) => {
        try {
          const code = getCountryCallingCode(c);
          return `+${code}` === value;
        } catch {
          return false;
        }
      });
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [value]);

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setOpen(false);
    try {
      const callingCode = getCountryCallingCode(countryCode as any);
      onChange(`+${callingCode}`);
    } catch (error) {
      console.error("Error getting country calling code:", error);
    }
  };

  // Get all countries and their calling codes
  const countries = getCountries();
  const countryOptions = countries
    .map((country) => {
      try {
        const callingCode = getCountryCallingCode(country);
        const displayName = regionNames.of(country) || country;
        return {
          code: country,
          name: displayName,
          callingCode: `+${callingCode}`,
          flag: country,
        };
      } catch {
        return null;
      }
    })
    .filter((c): c is NonNullable<typeof c> => c !== null)
    .sort((a, b) => {
      // India first, then alphabetical by name
      if (a.code === "IN") return -1;
      if (b.code === "IN") return 1;
      return a.name.localeCompare(b.name);
    });

  const selectedOption = countryOptions.find((c) => c.code === selectedCountry);

  return (
    <div className={className}>
      {!suppressLabel && <Label htmlFor="countryCode">Country Code *</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedOption ? (
              <div className="flex items-center gap-2">
                <span className="text-xl leading-none">
                  {String.fromCodePoint(
                    ...[...selectedOption.flag].map(
                      (c) => 0x1f1a5 + c.charCodeAt(0),
                    ),
                  )}
                </span>
                <span>{selectedOption.callingCode}</span>
              </div>
            ) : (
              "Select code..."
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search country..." />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryOptions.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={country.code}
                    keywords={[country.name, country.callingCode]}
                    onSelect={handleCountrySelect}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedCountry === country.code
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-xl leading-none">
                        {String.fromCodePoint(
                          ...[...country.flag].map(
                            (c) => 0x1f1a5 + c.charCodeAt(0),
                          ),
                        )}
                      </span>
                      <span className="font-medium">{country.callingCode}</span>
                      <span className="text-muted-foreground">
                        - {country.name}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
